import { appSchema } from 'shared/schema/appSchema';
import { serializeLexical } from 'shared/workflow/getArgumentValue/serializeLexical';
import { castArgumentDataType } from '@shared/workflow/castArgumentDataType.js';
import getDirectStepAttributes from './getDirectStepAttributes';
import getIteratorAttributes from './getIteratorAttributes';
import getScheduleAttributes from './getScheduleAttributes';
import getTransformAttributes from './getTransformAttributes';

const systemAttributes = appSchema?.general?.systemAttributes;

const getAttributes = ({ stepId, appId, apps, workflow, output, functionInputArgumentDefs = [], functionInputTestArguments = [], workflowArgumentDefs = [], integrationMetadataDefs = [], installationMetadata = {}, environment }) => {
  const app = apps?.find((d) => d?.id === appId);
  const steps = workflow?.steps;
  const step = steps?.find((s) => s?.id === stepId);
  const action = step?.actionId ? app?.actions?.find((a) => a?.id === step?.actionId) : null;

  if (stepId === 'functionInputs') {
    return functionInputArgumentDefs?.map((n) => {
      let value = serializeLexical(functionInputTestArguments?.[n?.key]?.value, { steps: [] })?.value;

      if (value !== undefined && value !== null && n?.dataType) {
        try {
          value = castArgumentDataType({
            value,
            dataType: n.dataType,
            itemDataType: n.itemDataType
          });
        } catch (error) {
          console.warn(`Failed to cast functionInput ${n?.key} to ${n?.dataType}:`, error.message);
        }
      }

      return { ...n, key: n?.key, value, outputs: [value] };
    });
  }

  if (stepId === 'workflowArguments') {
    const argumentEnvironment = workflow?.meta?.argumentEnvironments?.find((n) => n?.environmentId === environment?.id);
    const argumentEnvironmentValues = argumentEnvironment?.arguments || {};

    return workflowArgumentDefs?.map((n) => ({
      ...n,
      key: n?.key,
      outputs: [argumentEnvironmentValues?.[n?.key]],
      value: argumentEnvironmentValues?.[n?.key]
    }));
  }

  if (stepId === 'integrationMetadata') {
    return integrationMetadataDefs?.map((n) => ({
      ...n,
      key: n?.key,
      outputs: [installationMetadata?.[n?.key]],
      value: installationMetadata?.[n?.key]
    }));
  }

  if (stepId === 'system') {
    // installationId only exists for embedded workflows (those tied to an integration), so only
    // offer it as a selectable system variable there.
    const isEmbeddedWorkflow = Boolean(workflow?.integrationId);
    return systemAttributes
      .filter((n) => n.value !== 'installationId' || isEmbeddedWorkflow)
      .map((n) => ({
        ...n,
        key: n.value,
        category: n.category,
        // The runtime always resolves an environment name (falling back to the company default),
        // so when no environment is selected in the editor, mirror the server-computed value.
        outputs: [n.value === 'environment' ? (environment?.name ?? workflow?.variables?.system?.run?.environment) : workflow?.variables?.system?.[n.category]?.[n.value]]
      }));
  }

  if (stepId === 'memory') {
    const memoryVariables = workflow?.variables?.memory || {};
    const { global = {}, environment = {}, installation = {}, ...localMemory } = memoryVariables;

    const flattenObject = (obj = {}, opts = { prefix: '', isGlobal: false }) => {
      const { prefix, isGlobal } = opts;
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          return [
            ...acc,
            {
              key: newKey,
              dataType: 'object',
              outputs: obj[key],
              isGlobal
            },
            ...flattenObject(obj[key], { prefix: newKey, isGlobal })
          ];
        } else {
          return [
            ...acc,
            {
              key: newKey,
              dataType: typeof obj[key] === 'string' ? 'string' : Array.isArray(obj[key]) ? 'array' : typeof obj[key],
              outputs: [obj[key]],
              isGlobal
            }
          ];
        }
      }, []);
    };

    const memory = flattenObject(localMemory);
    const globalMemory = flattenObject(global, { isGlobal: true }).map((item) => ({
      ...item,
      key: `global.${item.key}`
    }));
    const environmentMemory = flattenObject(environment).map((item) => ({
      ...item,
      key: `environment.${item.key}`
    }));
    const installationMemory = flattenObject(installation).map((item) => ({
      ...item,
      key: `installation.${item.key}`
    }));

    return [...memory, ...globalMemory, ...environmentMemory, ...installationMemory];
  }

  if (app?.slug === 'schedule') {
    return getScheduleAttributes();
  }

  if (app?.slug === 'iterate') {
    return getIteratorAttributes({ stepId, apps, steps });
  }

  if (app?.slug === 'transform') {
    return getTransformAttributes({ stepId, apps, steps });
  }

  return getDirectStepAttributes({ actionId: action?.triggerType === 'longPolling' ? action?.longPolling?.extraAction?.id || action?.longPolling?.actionId : null, stepId, appId, steps, apps, output });
};

export default getAttributes;
